import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../context/ToastContext';

const MyCertificates = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [isCLM, setIsCLM] = useState(false);

  useEffect(() => {
    fetchCertificates();
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u?.companyId && u?.id) {
      api
        .get(`/departments?companyId=${u.companyId}`)
        .then((res) => setIsCLM((res.data || []).some((d) => d.managerId === u.id)))
        .catch(() => setIsCLM(false));
    }
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await api.get('/certificates/my/all');
      setCertificates(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async () => {
    if (certificates.length === 0) return;
    setViewing(true);
    try {
      const latest = certificates[0];
      // Folosim noul endpoint full-sheet care concatenează fișa principală cu fișele postului
      // semnate (dacă există). Fallback la endpoint-ul vechi dacă lipsește employeeId.
      const fullSheetUrl = latest.employeeId
        ? `/job-descriptions/employee/${latest.employeeId}/full-sheet`
        : `/certificates/${latest.trainingId}/download?inline=true`;
      const response = await api.get(fullSheetUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(t('toasts.errorGeneric'), 'error');
    } finally {
      setViewing(false);
    }
  };

  const handleDownload = async () => {
    if (certificates.length === 0) return;
    setDownloading(true);
    try {
      // Descarcă de pe primul certificat (toate au același PDF actualizat)
      const latest = certificates[0];
      const fullSheetUrl = latest.employeeId
        ? `/job-descriptions/employee/${latest.employeeId}/full-sheet`
        : `/certificates/${latest.trainingId}/download`;
      const response = await api.get(fullSheetUrl, { responseType: 'blob' });
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const fileName =
        `Fisa-Instruire-${user.firstName || ''}-${user.lastName || ''}-${latest.certificateNumber}.pdf`.replace(
          /\s+/g,
          '-'
        );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(t('toasts.errorGeneric'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <Spinner label={t('myCertificatesExt.loadingSheet')} />;
  }

  if (certificates.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('myCertificates.title')} subtitle={t('myCertificates.subtitle')} />
        <EmptyState
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-4.5A3.375 3.375 0 0012.75 10.5h-1.5A3.375 3.375 0 007.5 14.25v4.5m9-9V6a3 3 0 00-3-3H10.5a3 3 0 00-3 3v3.75"
              />
            </svg>
          }
          title={t('myCertificates.noSheets')}
          description={t('myCertificatesExt.noSheetsDescription')}
        />
      </div>
    );
  }

  const latest = certificates[0];
  const allSigs = certificates.flatMap((c) => c.training?.signatures || []);
  const lastUpdate = new Date(
    Math.max(...certificates.map((c) => new Date(c.issueDate)))
  ).toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // Statusuri per tip
  const ssmTrainings = certificates.filter((c) => c.training?.course?.type?.endsWith('_SSM'));
  const suTrainings = certificates.filter((c) => c.training?.course?.type?.endsWith('_SU'));

  return (
    <div className="space-y-6">
      <PageHeader title={t('myCertificates.title')} subtitle={t('myCertificates.subtitleFull')} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch max-w-4xl">
        <Card className="border-2 border-primary-100 dark:border-primary-700 flex flex-col gap-4 h-full">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center shrink-0">
              <svg
                className="w-8 h-8 text-primary-600 dark:text-primary-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {t('myCertificatesExt.sheetHeading')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Nr. {latest.certificateNumber}
              </p>
            </div>
          </div>

          <div className="space-y-2 text-sm border-t pt-4">
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                {t('myCertificatesExt.lastUpdate')}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{lastUpdate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                {t('myCertificatesExt.ssmTrainings')}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {ssmTrainings.length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                {t('myCertificatesExt.suTrainings')}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {suTrainings.length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                {t('myCertificatesExt.totalSignatures')}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {allSigs.length}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="primary"
              size="md"
              className="flex-1"
              onClick={handleView}
              disabled={viewing || downloading}
            >
              {viewing ? (
                t('myCertificates.openingSheet')
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {t('myCertificates.viewSheet')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleDownload}
              disabled={downloading || viewing}
              title={t('myCertificates.downloadSheet')}
            >
              {downloading ? (
                t('myCertificates.downloading')
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
              )}
            </Button>
          </div>
        </Card>

        {/* Status semnături per instruire — coloana dreapta */}
        <Card className="h-full">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
            {t('myCertificatesExt.signatureStatus')}
          </h3>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {certificates.map((c) => {
              const tr = c.training;
              const type = tr?.course?.type;
              const sigs = tr?.signatures || [];
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              // Matrice EMPLOYEE și CLM. CLM ca persoană instruită = matricea DIRECTOR_GENERAL: nu se
              // poate semna pe sine la slotul de conducător → ADMINUL preia verificarea (MEREU adminul,
              // indiferent dacă firma are DG sau alți CLM activi). Sincron cu backend `signatureResolver`.
              const MATRIX_EMPLOYEE = {
                IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
                ILM_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
                IP_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
                SUPLIMENTARE_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
                IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
                ILM_SU: ['EMPLOYEE', 'MANAGER'],
                IP_SU: ['EMPLOYEE', 'MANAGER'],
                SUPLIMENTARE_SU: null,
              };
              const MATRIX_CLM = {
                IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
                ILM_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
                IP_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
                SUPLIMENTARE_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
                IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
                ILM_SU: ['EMPLOYEE', 'ADMIN'],
                IP_SU: ['EMPLOYEE', 'ADMIN'],
                SUPLIMENTARE_SU: null,
              };
              const MATRIX_COMPANY_ADMIN = {
                IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
                ILM_SSM: null,
                IP_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
                SUPLIMENTARE_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
                IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
                ILM_SU: null,
                // SU = domeniul PSI → verificator Cadru Tehnic PSI, nu SSM (sincron cu backend).
                IP_SU: ['EMPLOYEE', 'CADRU_PSI'],
                SUPLIMENTARE_SU: null,
              };
              // Backend-ul calculează _isTraineeCLM din ClmMandate (sursă corectă, vezi
              // computeIsTraineeCLM) — folosit prioritar. `isCLM` (din Department.managerId,
              // depreciat) rămâne doar ca fallback dacă flag-ul lipsește dintr-un răspuns vechi.
              const traineeIsCLM = tr?._isTraineeCLM ?? isCLM;
              const matrix =
                u.role === 'COMPANY_ADMIN'
                  ? MATRIX_COMPANY_ADMIN
                  : traineeIsCLM
                    ? MATRIX_CLM
                    : MATRIX_EMPLOYEE;
              const expected = matrix[type];
              if (expected === null) {
                return (
                  <li key={c.id} className="py-1.5 flex items-center justify-between gap-3 text-xs">
                    <span className="text-slate-700 dark:text-slate-300 truncate">
                      {tr?.course?.title || '—'}
                    </span>
                    <span className="text-slate-400 dark:text-slate-400 whitespace-nowrap">
                      {t('myCertificatesExt.notApplicable')}
                    </span>
                  </li>
                );
              }
              const safeExpected = expected || ['EMPLOYEE'];
              const slotFilled = (slot) =>
                sigs.some((s) => {
                  if (slot === 'EMPLOYEE')
                    return s.signerRole === 'EMPLOYEE' && s.signerId === u.id;
                  if (slot === 'SSM')
                    return (
                      ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(s.signerRole) ||
                      s.signer?.isSSMInstructor
                    );
                  if (slot === 'CADRU_PSI') return s.signer?.isCadruTehnicPSI;
                  if (slot === 'ADMIN') return s.signerRole === 'COMPANY_ADMIN';
                  if (slot === 'DIRECTOR') return s.signerRole === 'DIRECTOR_GENERAL';
                  if (slot === 'MANAGER')
                    return (
                      s.signerRole === 'MANAGER' ||
                      (s.signerRole === 'EMPLOYEE' && s.signerId !== u.id)
                    );
                  return false;
                });
              // Logică unificată cu EmployeeSheets: slot-urile non-EMPLOYEE sunt reutilizabile
              const seen = new Set();
              let next = null;
              for (const slot of safeExpected) {
                if (slot === 'EMPLOYEE') {
                  if (!slotFilled('EMPLOYEE')) {
                    next = slot;
                    break;
                  }
                  continue;
                }
                if (seen.has(slot)) continue;
                seen.add(slot);
                if (!slotFilled(slot)) {
                  next = slot;
                  break;
                }
              }
              const labels = {
                EMPLOYEE: t('myCertificatesExt.signerLabels.EMPLOYEE'),
                SSM: t('myCertificatesExt.signerLabels.SSM'),
                CADRU_PSI: t('myCertificatesExt.signerLabels.CADRU_PSI'),
                MANAGER: t('myCertificatesExt.signerLabels.MANAGER'),
                ADMIN: t('myCertificatesExt.signerLabels.ADMIN'),
                DIRECTOR: t('myCertificatesExt.signerLabels.DIRECTOR'),
              };
              return (
                <li key={c.id} className="py-1.5 flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-700 truncate">{tr?.course?.title || '—'}</span>
                  {next ? (
                    <span className="text-warning-600 dark:text-warning-300 whitespace-nowrap">
                      {t('myCertificatesExt.waitingFor')} {labels[next]}
                    </span>
                  ) : (
                    <span className="text-accent-600 dark:text-accent-300 whitespace-nowrap">
                      ✓ {t('myCertificatesExt.complete')}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default MyCertificates;
